import React from "react";
import { Card, Button, Avatar } from "../../design-system";
import { Icon, SupHeader, SupSection, SupKpi, SupBars, CatIcon } from "./atoms";
import { SUP_TICKETS, SUP_ASSETS, SUP_USERS, SUP_COMPANIES, CAT, ticketsForAsset, ticketsForResource, SUP_RESOURCES } from "./data";
import { useSoporteNav } from "./nav";

/**
 * Soporte IT · Reportes — indicadores de soporte (tendencia, distribución,
 * top servicios/activos/usuarios y estado del parque). UI-only.
 */
function SoporteReportes() {
  document.title = "Reportes · Soporte IT | Kronaly";
  const nav = useSoporteNav();

  // tickets por mes (sintético)
  const months = [
    { m: "Ene", v: 18 }, { m: "Feb", v: 24 }, { m: "Mar", v: 31 }, { m: "Abr", v: 27 }, { m: "May", v: 34 }, { m: "Jun", v: 16 },
  ];
  const maxM = Math.max(...months.map((d) => d.v));

  const catCount = {};
  SUP_TICKETS.forEach((t) => { catCount[t.cat] = (catCount[t.cat] || 0) + 1; });
  const catBars = Object.entries(catCount).map(([c, v]) => ({ label: CAT[c]?.label || c, value: v, icon: CAT[c]?.icon })).sort((a, b) => b.value - a.value);

  const companyBars = SUP_COMPANIES.map((c) => ({ label: c, value: SUP_TICKETS.filter((t) => t.company === c).length })).sort((a, b) => b.value - a.value);

  const topServices = SUP_RESOURCES.map((r) => ({ r, count: ticketsForResource(r.id).length })).filter((x) => x.count > 0).sort((a, b) => b.count - a.count).slice(0, 5);
  const topAssets = SUP_ASSETS.map((a) => ({ a, count: ticketsForAsset(a.id).length })).filter((x) => x.count > 0).sort((a, b) => b.count - a.count).slice(0, 5);
  const topUsers = SUP_USERS.map((u) => ({ u, count: SUP_TICKETS.filter((t) => t.user === u.id).length })).filter((x) => x.count > 0).sort((a, b) => b.count - a.count).slice(0, 5);

  const sinGarantia = SUP_ASSETS.filter((a) => new Date(a.warranty) < new Date("2026-06-11"));
  const pdteDevol = SUP_ASSETS.filter((a) => a.status === "pdte_devolucion" || a.status === "pdte_entrega");

  return (
    <div className="kr-page">
      <SupHeader
        eyebrow="Soporte IT"
        title="Reportes"
        subtitle="Indicadores de soporte. La prioridad está en software y servicios, no solo en hardware."
        actions={<Button variant="secondary" icon="Download">Exportar PDF</Button>}
      />

      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 14 }} data-responsive-grid="4">
        <SupKpi label="Tickets este mes" value="16" icon="ticket" hint="↓ 18 en mayo" />
        <SupKpi label="Tiempo medio resolución" value="6,4" unit="h" icon="clock" accent="var(--kr-success-600)" />
        <SupKpi label="Activos fuera de garantía" value={sinGarantia.length} icon="alert" accent="var(--kr-danger-600)" />
        <SupKpi label="Equipos pendientes devolución" value={pdteDevol.length} icon="refresh" accent="var(--kr-warning-600)" />
      </div>

      {/* tickets por mes */}
      <Card>
        <SupSection eyebrow="Tendencia" title="Tickets por mes" right={<span style={{ fontSize: 11, color: "var(--kr-fg-muted)", fontFamily: "var(--kr-font-mono)" }}>2026</span>} />
        <div style={{ display: "grid", gridTemplateColumns: "repeat(6, 1fr)", gap: 16, alignItems: "end", height: 180, marginTop: 20 }}>
          {months.map((d, i) => (
            <div key={i} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 8, height: "100%" }}>
              <div style={{ flex: 1, width: "100%", display: "flex", alignItems: "end", position: "relative" }}>
                <div style={{ position: "absolute", inset: 0, background: "var(--kr-neutral-100)", borderRadius: 6 }} />
                <div style={{ position: "absolute", left: 0, right: 0, bottom: 0, height: `${(d.v / maxM) * 100}%`, background: "var(--kr-action)", borderRadius: 6, opacity: i === months.length - 1 ? 0.45 : 1 }} />
                <span style={{ position: "absolute", top: -20, left: 0, right: 0, textAlign: "center", fontFamily: "var(--kr-font-mono)", fontSize: 12, fontWeight: 600, color: "var(--kr-fg-strong)" }}>{d.v}</span>
              </div>
              <span style={{ fontSize: 11, color: "var(--kr-fg-muted)", fontFamily: "var(--kr-font-mono)" }}>{d.m}</span>
            </div>
          ))}
        </div>
      </Card>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, alignItems: "start" }} data-responsive-grid="2">
        <Card>
          <SupSection eyebrow="Distribución" title="Por categoría" />
          <div style={{ marginTop: 18 }}><SupBars data={catBars} /></div>
        </Card>
        <Card>
          <SupSection eyebrow="Distribución" title="Por empresa" />
          <div style={{ marginTop: 18 }}><SupBars data={companyBars} accent="var(--kr-info-500)" /></div>
        </Card>
      </div>

      {/* reportes destacados */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, alignItems: "start" }} data-responsive-grid="2">
        <Card style={{ borderColor: "var(--kr-warning-100)" }}>
          <SupSection eyebrow="Reporte clave" title="Top servicios con más incidencias" right={<Icon name="zap" size={16} stroke="var(--kr-warning-600)" />} />
          <div style={{ marginTop: 14 }}>
            {topServices.map((x, i) => (
              <div key={x.r.id} onClick={() => nav.go("recursos")} style={{ display: "flex", alignItems: "center", gap: 12, padding: "11px 0", borderTop: i === 0 ? "none" : "1px solid var(--kr-border-subtle)", cursor: "pointer" }}>
                <span style={{ fontFamily: "var(--kr-font-mono)", fontSize: 13, fontWeight: 700, color: "var(--kr-fg-subtle)", width: 18 }}>{i + 1}</span>
                <CatIcon cat={x.r.cat} size={28} />
                <span style={{ flex: 1, fontSize: 13, fontWeight: 500, color: "var(--kr-fg-strong)" }}>{x.r.name}</span>
                <span style={{ fontFamily: "var(--kr-font-mono)", fontSize: 13, fontWeight: 700, color: "var(--kr-warning-700)" }}>{x.count}</span>
              </div>
            ))}
          </div>
        </Card>

        <Card>
          <SupSection eyebrow="Reporte clave" title="Top activos con más incidencias" right={<Icon name="package" size={16} stroke="var(--kr-fg-muted)" />} />
          <div style={{ marginTop: 14 }}>
            {topAssets.map((x, i) => (
              <div key={x.a.id} onClick={() => nav.go("activo", { id: x.a.id })} style={{ display: "flex", alignItems: "center", gap: 12, padding: "11px 0", borderTop: i === 0 ? "none" : "1px solid var(--kr-border-subtle)", cursor: "pointer" }}>
                <span style={{ fontFamily: "var(--kr-font-mono)", fontSize: 13, fontWeight: 700, color: "var(--kr-fg-subtle)", width: 18 }}>{i + 1}</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 500, color: "var(--kr-fg-strong)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{x.a.brand} {x.a.model}</div>
                  <div style={{ fontSize: 11, color: "var(--kr-fg-muted)", fontFamily: "var(--kr-font-mono)" }}>{x.a.code}</div>
                </div>
                <span style={{ fontFamily: "var(--kr-font-mono)", fontSize: 13, fontWeight: 700, color: "var(--kr-fg-strong)" }}>{x.count}</span>
              </div>
            ))}
          </div>
        </Card>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, alignItems: "start" }} data-responsive-grid="2">
        <Card>
          <SupSection eyebrow="Usuarios" title="Usuarios con más tickets" />
          <div style={{ marginTop: 14 }}>
            {topUsers.map((x, i) => (
              <div key={x.u.id} onClick={() => nav.go("usuario", { id: x.u.id })} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 0", borderTop: i === 0 ? "none" : "1px solid var(--kr-border-subtle)", cursor: "pointer" }}>
                <Avatar name={x.u.name} size={28} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 500, color: "var(--kr-fg-strong)" }}>{x.u.name}</div>
                  <div style={{ fontSize: 11, color: "var(--kr-fg-muted)" }}>{x.u.dept} · {x.u.company}</div>
                </div>
                <span style={{ fontFamily: "var(--kr-font-mono)", fontSize: 13, fontWeight: 700, color: "var(--kr-fg-strong)" }}>{x.count}</span>
              </div>
            ))}
          </div>
        </Card>

        <Card>
          <SupSection eyebrow="Inventario" title="Estado del parque de equipos" />
          <div style={{ marginTop: 14, display: "flex", flexDirection: "column", gap: 10 }}>
            {[
              { label: "Equipos disponibles", value: SUP_ASSETS.filter((a) => a.status === "disponible").length, icon: "checkCircle", tone: "var(--kr-success-700)" },
              { label: "Equipos en reparación", value: SUP_ASSETS.filter((a) => a.status === "reparacion").length, icon: "wrench", tone: "var(--kr-pending-600)" },
              { label: "Pendientes de devolución", value: pdteDevol.length, icon: "refresh", tone: "var(--kr-warning-700)" },
              { label: "Fuera de garantía", value: sinGarantia.length, icon: "alert", tone: "var(--kr-danger-700)" },
            ].map((s, i) => (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 12px", border: "1px solid var(--kr-border-subtle)", borderRadius: 10 }}>
                <Icon name={s.icon} size={16} stroke={s.tone} />
                <span style={{ flex: 1, fontSize: 13, color: "var(--kr-fg-strong)" }}>{s.label}</span>
                <span style={{ fontFamily: "var(--kr-font-mono)", fontSize: 15, fontWeight: 700, color: s.tone }}>{s.value}</span>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
}

export default SoporteReportes;
